import React from 'react';
import { Box, useToken } from '@chakra-ui/react';

interface CompassRoseProps {
  size?: number;
}

const ACCENT_RGB = 'var(--chakra-colors-brand-primary-rgb)';

const CompassRose = ({ size = 80 }: CompassRoseProps) => {
  const [cream, accentShade] = useToken('colors', ['text.onBrand', 'brand.primary.shade']);

  const ticks = Array.from({ length: 12 }, (_, i) => {
    const angle = i * 30;
    const isMajor = i % 3 === 0;
    return (
      <line
        key={i}
        x1="36"
        y1="6"
        x2="36"
        y2={isMajor ? 11 : 9}
        stroke={cream}
        strokeOpacity={isMajor ? 0.85 : 0.45}
        strokeWidth={isMajor ? 1.4 : 0.9}
        strokeLinecap="round"
        transform={`rotate(${angle} 36 36)`}
      />
    );
  });

  return (
    <Box
      width={`${size}px`}
      height={`${size}px`}
      borderRadius="50%"
      bg="brand.primary"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow={`0 10px 30px rgba(${ACCENT_RGB}, 0.35)`}
      sx={{
        animation: 'rose-spin 40s linear infinite',
        '@keyframes rose-spin': {
          to: { transform: 'rotate(360deg)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      <svg viewBox="0 0 72 72" width={size * 0.82} height={size * 0.82} fill="none" aria-hidden="true">
        <circle cx="36" cy="36" r="30" stroke={cream} strokeOpacity="0.4" strokeWidth="1" />
        <circle cx="36" cy="36" r="22" stroke={cream} strokeOpacity="0.22" strokeWidth="1" />
        {ticks}

        {/* Minor pointers (NE, SE, SW, NW) */}
        {[45, 135, 225, 315].map((angle) => (
          <polygon
            key={`minor-${angle}`}
            points="36,12 38.4,36 36,33 33.6,36"
            fill={cream}
            fillOpacity="0.35"
            transform={`rotate(${angle} 36 36)`}
          />
        ))}

        {/* East / West */}
        <polygon points="10 36 36 33 31 36 36 39" fill={cream} fillOpacity="0.55" />
        <polygon points="62 36 36 33 41 36 36 39" fill={cream} fillOpacity="0.55" />

        {/* South (muted warm) */}
        <polygon points="36 62 40 36 36 40 32 36" fill={accentShade} fillOpacity="0.9" />

        {/* North (bright) */}
        <polygon points="36 10 40 36 36 32 32 36" fill={cream} />

        <text
          x="36"
          y="8"
          textAnchor="middle"
          fontSize="6"
          fontWeight="700"
          letterSpacing="0.15em"
          fill={cream}
          fillOpacity="0.95"
          fontFamily="'Space Grotesk', sans-serif"
        >
          N
        </text>

        <circle cx="36" cy="36" r="3" fill={cream} />
        <circle cx="36" cy="36" r="1.4" fill={accentShade} />
      </svg>
    </Box>
  );
};

export default CompassRose;
